import { compulsoryColumns, excludedColumns } from "@Components/FileColumnsTreeView/constants";
import axiosWrapper from "@AxiosWrapper";
import { getBackendUrl } from "@Utils";
import type { GetDistinctFilePropertyNamesResponse } from "@ServerTypes/queries/queries.schema";

/** A grid column referenced either by its plain field name or by a definition that carries one. */
export type ColumnRef = string | { field?: unknown };

/**
 * Drops the excluded and compulsory columns. Generic because callers pass either the plain field
 * names (`string[]`) or `{ field, label }` pairs, and each expects its own element type back —
 * `SearchSet1/index.tsx` feeds the filtered field names straight into a `string[]` prop.
 */
export const getFilteredColumns = <T extends ColumnRef>(columns: T[]): T[] =>
  columns.filter((col: ColumnRef) => {
    // `col` is either the field name itself or a column definition holding it.
    let column: string = col as string;
    if (typeof column != "string") {
      column = (col as { field: string }).field;
    }
    let includeColumn = true;

    excludedColumns.forEach((excludedColumn) => {
      if (excludedColumn.field == column && includeColumn) {
        includeColumn = false;
      }
    });

    compulsoryColumns.forEach((compulsoryColumn) => {
      if (compulsoryColumn.field == column && includeColumn) {
        includeColumn = false;
      }
    });

    return includeColumn;
  });

export const getFilePropertyNames = async (fileVersionId: string | string[]) => {
  const propertyNames = await axiosWrapper.post<GetDistinctFilePropertyNamesResponse>(
    `${getBackendUrl()}/queries/getDistinctFilePropertyNames`,
    {
      fileVersionId: Array.isArray(fileVersionId) ? fileVersionId : [fileVersionId],
    },
  );

  let columns: string[] = [];
  if (propertyNames.data.status) {
    // SCHEMA GAP: getDistinctFilePropertyNamesResponse declares the names under `result`, but the
    // handler returns them under an undeclared `data` key.
    columns = getFilteredColumns(
      (propertyNames.data as GetDistinctFilePropertyNamesResponse & { data: string[] }).data,
    );
  }
  return [columns, propertyNames.data];
};

export const convertToDotNotation = (
  obj: Record<string, unknown>,
  parentKey = "",
  result: Record<string, unknown> = {},
) => {
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const newKey = parentKey ? `${parentKey}.${key}` : key;
      const checkForObjectInCustomFields = typeof obj[key] === "object" && parentKey != "customProperties";

      if (
        typeof obj[key] === "object" &&
        obj[key] !== null &&
        !Array.isArray(obj[key]) &&
        checkForObjectInCustomFields
      ) {
        convertToDotNotation(obj[key] as Record<string, unknown>, newKey, result);
      } else {
        result[newKey] = obj[key];
      }
    }
  }
  return result;
};

/**
 * Flattens the merged model-properties map `{ [fileVersionId]: { [nodeId]: node } }` to grid rows.
 * Callers hold that map under several node types (ModelPropertyNode, MergedNode), so the per-file
 * bucket is only `object` here and each node is read as an open record.
 */
export const getModifiedNodesList = (modelProperties: Record<string, object>): Record<string, unknown>[] => {
  return Object.values(modelProperties)
    .flatMap((v) => Object.values(v) as Record<string, unknown>[])
    .map((data) => ({
      id: `${data.fileVersionId}_${data.nodeId}`,
      ...convertToDotNotation(data),
    }));
};

export const extractPropertyPartialName = (field: string | null | undefined): string => {
  // The name is genuinely optional: an incomplete query rule (no field selected)
  // has a null field, so there is no partial name to extract.
  if (!field) {
    return "";
  }
  const rootProperty = field.split(".");
  const value = rootProperty.slice(1).join(".").split("/");
  if (value.length > 1) {
    return value.slice(1).join("/");
  }
  // split() always yields at least one element, but noUncheckedIndexedAccess types
  // value[0] as string | undefined; coalesce so the return type stays a plain string.
  return value[0] ?? "";
};
